import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, BellRing, ChevronDown, ChevronRight, Clock, Download, Loader2, Printer, TrendingDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKETS = [
  { key: "not_due", label: "Not Due", cls: "text-foreground" },
  { key: "0_30", label: "0-30 Days", cls: "text-emerald-400" },
  { key: "31_60", label: "31-60 Days", cls: "text-amber-400" },
  { key: "61_90", label: "61-90 Days", cls: "text-orange-400" },
  { key: "90_plus", label: "90+ Days", cls: "text-red-400" },
];

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const todayStr = () => new Date().toISOString().split("T")[0];

const ReceivablesAgingPage = () => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState(todayStr());
  const [expanded, setExpanded] = useState({});
  const [reminding, setReminding] = useState("");
  const canManage = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/invoices/aging`, { params: { as_of: asOf } });
        setReport(data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to load aging report");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [asOf]);

  const sendReminder = async (inv) => {
    setReminding(inv.id);
    try {
      const { data } = await axios.post(`${API_URL}/invoices/${inv.id}/send-reminder`);
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setReminding("");
    }
  };

  const exportCsv = () => {
    const header = ["Client", ...BUCKETS.map((b) => b.label), "Total Outstanding", "Invoices", "Oldest (days)"];
    const rows = report.clients.map((c) => [c.client_name, ...BUCKETS.map((b) => c.buckets[b.key]), c.total_outstanding, c.invoice_count, c.oldest_days]);
    rows.push(["TOTAL", ...BUCKETS.map((b) => report.totals[b.key]), report.grand_total, report.invoice_count, ""]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `receivables-aging-${report.as_of}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-6 animate-fade-in print:space-y-3" data-testid="aging-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link to="/dashboard/invoices" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground print:hidden" data-testid="aging-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Invoices
          </Link>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3 mt-1">
            <TrendingDown className="w-8 h-8 text-chart-3" />
            Receivables Aging Report
          </h1>
          <p className="text-muted-foreground mt-1">Outstanding invoice balances by days past due, per client</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" /> As of
            <Input type="date" value={asOf} max={todayStr()} onChange={(e) => setAsOf(e.target.value || todayStr())} className="bg-secondary/50 border-border h-9 w-40" data-testid="aging-asof-input" />
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!report} data-testid="aging-export-csv"><Download className="w-4 h-4 mr-2" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="aging-print"><Printer className="w-4 h-4 mr-2" />Print</Button>
        </div>
      </div>

      {loading || !report ? (
        <div className="flex items-center justify-center h-64 text-primary animate-pulse font-heading text-xl">Loading aging report...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card className="border-primary/40 bg-primary/10">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Outstanding</div>
                <div className="font-mono text-xl font-bold text-primary mt-1" data-testid="aging-grand-total">{fmt(report.grand_total)}</div>
                <div className="text-xs text-muted-foreground mt-1" data-testid="aging-invoice-count">{report.invoice_count} invoices · {report.clients.length} clients</div>
              </CardContent>
            </Card>
            {BUCKETS.map((b) => (
              <Card key={b.key} className="border-border bg-card">
                <CardContent className="p-4">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{b.label}</div>
                  <div className={`font-mono text-xl font-bold mt-1 ${b.cls}`} data-testid={`aging-total-${b.key}`}>{fmt(report.totals[b.key])}</div>
                  <div className="text-xs text-muted-foreground mt-1">{report.grand_total ? Math.round((report.totals[b.key] / report.grand_total) * 100) : 0}% of total</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-border bg-card">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Client</TableHead>
                    {BUCKETS.map((b) => <TableHead key={b.key} className="text-muted-foreground uppercase text-xs tracking-wider text-right">{b.label}</TableHead>)}
                    <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Total Due</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.clients.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No outstanding receivables. All invoices are paid.</TableCell></TableRow>
                  ) : report.clients.map((c) => (
                    <ClientRows key={c.client_id} client={c} expanded={!!expanded[c.client_id]} onToggle={() => toggle(c.client_id)} canManage={canManage} reminding={reminding} onRemind={sendReminder} />
                  ))}
                  {report.clients.length > 0 && (
                    <TableRow className="border-border bg-secondary/40 font-bold hover:bg-secondary/40" data-testid="aging-totals-row">
                      <TableCell className="text-foreground">Total ({report.clients.length} clients)</TableCell>
                      {BUCKETS.map((b) => <TableCell key={b.key} className={`text-right font-mono ${b.cls}`}>{fmt(report.totals[b.key])}</TableCell>)}
                      <TableCell className="text-right font-mono text-primary">{fmt(report.grand_total)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const ClientRows = ({ client, expanded, onToggle, canManage, reminding, onRemind }) => (
  <>
    <TableRow className="border-border table-row-hover cursor-pointer" onClick={onToggle} data-testid={`aging-client-${client.client_id}`}>
      <TableCell className="text-foreground font-semibold">
        <span className="inline-flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          {client.client_name}
          <span className="text-xs text-muted-foreground font-normal">{client.invoice_count} inv · oldest {client.oldest_days}d</span>
        </span>
      </TableCell>
      {BUCKETS.map((b) => (
        <TableCell key={b.key} className={`text-right font-mono ${client.buckets[b.key] ? b.cls : "text-muted-foreground/40"}`} data-testid={`aging-client-${client.client_id}-${b.key}`}>
          {client.buckets[b.key] ? fmt(client.buckets[b.key]) : "—"}
        </TableCell>
      ))}
      <TableCell className="text-right font-mono font-bold text-foreground" data-testid={`aging-client-${client.client_id}-total`}>{fmt(client.total_outstanding)}</TableCell>
    </TableRow>
    {expanded && client.invoices.map((inv) => {
      const bucket = BUCKETS.find((b) => b.key === inv.bucket);
      return (
        <TableRow key={inv.id} className="border-border/50 bg-secondary/20 hover:bg-secondary/30" data-testid={`aging-invoice-${inv.id}`}>
          <TableCell className="pl-12">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-primary text-sm">{inv.invoice_number}</span>
              <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">{inv.status.replace("_", " ")}</Badge>
              <span className="text-xs text-muted-foreground">Due {inv.due_date}</span>
            </div>
          </TableCell>
          <TableCell colSpan={5} className="text-right">
            <span className={`text-xs font-semibold ${bucket?.cls}`}>{inv.days_past_due > 0 ? `${inv.days_past_due} days overdue` : "Not yet due"}</span>
            <span className="text-xs text-muted-foreground ml-3">Total {fmt(inv.total_amount)}{inv.amount_paid > 0 && ` · Paid ${fmt(inv.amount_paid)}`}</span>
            {inv.last_reminder_at && <span className="text-xs text-muted-foreground ml-3">Reminded {inv.last_reminder_at.split("T")[0]}</span>}
            {canManage && (
              <Button variant="ghost" size="icon" className="ml-2 h-7 w-7 text-amber-400 print:hidden" title="Email payment reminder" disabled={!!reminding}
                onClick={(e) => { e.stopPropagation(); onRemind(inv); }} data-testid={`aging-remind-${inv.id}`}>
                {reminding === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
              </Button>
            )}
          </TableCell>
          <TableCell className="text-right font-mono text-foreground" data-testid={`aging-invoice-${inv.id}-balance`}>{fmt(inv.balance_due)}</TableCell>
        </TableRow>
      );
    })}
  </>
);

export default ReceivablesAgingPage;
